import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';

import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { FotoPessoaPainel, OrdenacaoPessoasPainel } from './selecao-pessoas-painel.model';
import { filtrarPorTexto, ordenarPor } from './selecao-pessoas-painel.presenter';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Painel genérico de "selecionados" (padrão da Capa de PAT/Dívida Ativa): busca local, lista com
 * avatar + nome + subtítulo, remoção por item, ordenação configurável e contador — reaproveitado por
 * qualquer tela que monte uma seleção de pessoas a partir de um modal próprio. O painel não abre o
 * modal de busca (cada tela tem o seu, com regras de oferta diferentes); só emite {@link abrirSelecao}
 * no clique de "Adicionar" e o host decide como buscar e devolver a leva via `[(selecionados)]`.
 */
@Component({
    selector: 'app-selecao-pessoas-painel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, FormsModule, ButtonModule, DialogModule, IconFieldModule, InputIconModule, InputTextModule, RadioButtonModule, TableModule, Tooltip, PessoaAvatarComponent],
    templateUrl: './selecao-pessoas-painel.html',
    styleUrl: './selecao-pessoas-painel.scss'
})
export class SelecaoPessoasPainel<T> {
    /** Itens selecionados — dono do estado é o host (two-way). */
    readonly selecionados = model.required<T[]>();

    /** Chave de identidade do item (dedupe/remoção). */
    readonly chave = input.required<(item: T) => string | number>();
    /** Nome exibido na linha. */
    readonly nomeDe = input.required<(item: T) => string>();
    /** Subtítulo sob o nome (ex.: "CRM 001234/SP"). */
    readonly subtituloDe = input.required<(item: T) => string>();
    /** Identidade para resolver o retrato via `app-pessoa-avatar`; `null` usa o ícone de fallback. */
    readonly fotoDe = input<(item: T) => FotoPessoaPainel | null>(() => null);
    /** Ícone (classe PrimeIcons) do fallback quando `fotoDe` devolve `null` para o item. */
    readonly iconeSemFoto = input('pi pi-user');
    /** Critérios de ordenação oferecidos (radio "Ordenar por"); o primeiro é o padrão. */
    readonly ordenacoes = input.required<OrdenacaoPessoasPainel<T>[]>();
    /** Habilita o botão "Adicionar" — desabilitado quando o host ainda não tem contexto para buscar. */
    readonly podeAdicionar = input(true);
    /** Tooltip do "Adicionar" quando desabilitado (explica o que falta). */
    readonly tituloBotaoDesabilitado = input('');
    /** Texto do estado vazio. */
    readonly rotuloVazio = input('Nenhum selecionado. Use Adicionar para escolher na busca.');
    /** Sufixo do contador do rodapé: "{n} <sufixo>". */
    readonly sufixoContador = input('selecionado(s)');
    /** `aria-label` da tabela de selecionados. */
    readonly ariaLabelLista = input('Itens selecionados');

    /** Clique em "Adicionar" — o host abre o próprio modal de busca. */
    readonly abrirSelecao = output<void>();

    protected readonly buscaLocal = signal('');
    protected readonly ordenacaoIndice = signal(0);

    protected readonly ordenacaoAtual = computed<OrdenacaoPessoasPainel<T> | null>(() => this.ordenacoes()[this.ordenacaoIndice()] ?? null);

    /** Lista exibida: os selecionados recortados pela busca local e ordenados (paginação na p-table). */
    protected readonly selecionadosFiltrados = computed(() => {
        const nomeDe = this.nomeDe();
        const subtituloDe = this.subtituloDe();
        const filtrados = filtrarPorTexto(this.selecionados(), this.buscaLocal(), (item) => `${nomeDe(item)} ${subtituloDe(item)}`);
        const ordenacao = this.ordenacaoAtual();
        return ordenacao ? ordenarPor(filtrados, ordenacao.comparador) : filtrados;
    });

    protected readonly fotoAmpliada = signal<string | null>(null);
    protected readonly fotoAmpliadaVisivel = signal(false);

    protected remover(item: T): void {
        const chaveDe = this.chave();
        const chaveDoItem = chaveDe(item);
        this.selecionados.set(this.selecionados().filter((s) => chaveDe(s) !== chaveDoItem));
    }

    /** Esvazia a lista de uma vez; limpa também a busca — senão a lista "some" no próximo Adicionar. */
    protected limpar(): void {
        this.selecionados.set([]);
        this.buscaLocal.set('');
    }

    protected fotoParams(item: T): FotoPessoaPainel | null {
        return this.fotoDe()(item);
    }

    protected ampliarFoto(foto: string): void {
        this.fotoAmpliada.set(foto);
        this.fotoAmpliadaVisivel.set(true);
    }
}
